import "./simple-pdf-tool.css";
import "./pdf-reader.css";
import { openBrowserPdf, pdfErrorMessage, processWithConcurrency, renderPageBlob, renderPageIntoCanvas } from "./pdf-browser-core.js";

const root = document.querySelector("[data-pdf-reader]");

if (root) {
  const ids = ["readerStatus", "readerUploadPanel", "readerDropZone", "chooseReaderPdf", "readerFileInput", "readerWorkspace", "readerFileName", "readerPageCount", "readerPageTotal", "readerPrevious", "readerCurrentPage", "readerNext", "readerZoom", "readerFit", "readerRotate", "replaceReaderPdf", "readerThumbnails", "readerStage", "readerCanvas"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { document: null, pageCount: 0, currentPage: 1, zoom: 1, fit: false, rotation: 0, busy: false, thumbnails: [], renderTask: null, resizeTimer: null };

  function showStatus(message = "") { elements.readerStatus.textContent = message; elements.readerStatus.hidden = !message; }
  function updateControls() {
    elements.readerPrevious.disabled = state.busy || state.currentPage <= 1;
    elements.readerNext.disabled = state.busy || state.currentPage >= state.pageCount;
    elements.readerCurrentPage.disabled = state.busy; elements.readerCurrentPage.value = String(state.currentPage); elements.readerCurrentPage.max = String(state.pageCount);
    elements.readerZoom.disabled = state.busy; elements.readerFit.disabled = state.busy; elements.readerRotate.disabled = state.busy; elements.replaceReaderPdf.disabled = state.busy;
    elements.readerThumbnails.querySelectorAll("button").forEach((button, index) => button.classList.toggle("is-current", index + 1 === state.currentPage));
  }

  async function loadFile(file) {
    if (state.busy) return;
    showStatus(); state.busy = true; updateControls(); let loaded = null;
    try {
      loaded = await openBrowserPdf(file);
      revokeThumbnails(); if (state.document) await state.document.destroy();
      state.document = loaded.document; state.pageCount = loaded.pageCount; state.currentPage = 1; state.zoom = 1; state.fit = false; state.rotation = 0; loaded = null;
      elements.readerZoom.value = "1";
      elements.readerFileName.textContent = file.name || "document.pdf"; elements.readerPageCount.textContent = String(state.pageCount); elements.readerPageTotal.textContent = String(state.pageCount);
      elements.readerUploadPanel.hidden = true; elements.readerWorkspace.hidden = false;
      renderThumbnailShells(); await Promise.all([renderThumbnails(), renderCurrentPage()]);
      window.toolboxAnalytics?.track("tool_complete");
    } catch (error) { if (loaded?.document) await loaded.document.destroy(); showStatus(pdfErrorMessage(error)); }
    finally { state.busy = false; elements.readerFileInput.value = ""; updateControls(); }
  }

  function renderThumbnailShells() {
    elements.readerThumbnails.replaceChildren();
    for (let index = 1; index <= state.pageCount; index += 1) {
      const button = document.createElement("button"); button.type = "button"; button.dataset.page = String(index); button.setAttribute("aria-label", `Open page ${index}`);
      const preview = document.createElement("span"); preview.className = "reader-thumb-preview"; preview.textContent = "…";
      const label = document.createElement("span"); label.textContent = `Page ${index}`;
      button.append(preview, label); button.addEventListener("click", () => goToPage(index)); elements.readerThumbnails.append(button);
    }
  }

  async function renderThumbnails() {
    const pages = Array.from({ length: state.pageCount }, (_, index) => index + 1);
    await processWithConcurrency(pages, 3, async pageNumber => {
      const page = await state.document.getPage(pageNumber);
      try {
        const blob = await renderPageBlob(page, { dpi: 80, maxWidth: 120, maxHeight: 145 });
        const url = URL.createObjectURL(blob); state.thumbnails.push(url);
        const preview = elements.readerThumbnails.querySelector(`[data-page="${pageNumber}"] .reader-thumb-preview`);
        if (preview) { const image = document.createElement("img"); image.src = url; image.alt = ""; preview.replaceChildren(image); }
      } finally { page.cleanup(); }
    });
  }

  async function renderCurrentPage() {
    if (!state.document) return;
    const page = await state.document.getPage(state.currentPage);
    try {
      const rotation = (page.rotate + state.rotation) % 360;
      let scale = state.zoom;
      const natural = page.getViewport({ scale: 1, rotation });
      const widthScale = Math.max(.1, availableStageWidth() / natural.width);
      if (state.fit) {
        scale = Math.max(.1, Math.min(2, widthScale));
      } else if (state.zoom <= 1) {
        scale = Math.min(state.zoom, widthScale);
      }
      await renderPageIntoCanvas(page, elements.readerCanvas, { scale, rotation });
    } finally { page.cleanup(); }
    updateControls();
  }

  async function goToPage(value) {
    const page = Math.max(1, Math.min(state.pageCount, Number(value) || 1));
    if (page === state.currentPage) return;
    state.currentPage = page; elements.readerStage.scrollTo({ top: 0, left: 0 }); updateControls(); await renderCurrentPage();
  }
  function revokeThumbnails() { state.thumbnails.forEach(url => URL.revokeObjectURL(url)); state.thumbnails = []; }
  function availableStageWidth() {
    const style = getComputedStyle(elements.readerStage);
    const padding = (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);
    return Math.max(1, elements.readerStage.clientWidth - padding - 2);
  }

  elements.chooseReaderPdf.addEventListener("click", event => { event.stopPropagation(); elements.readerFileInput.click(); });
  elements.replaceReaderPdf.addEventListener("click", () => elements.readerFileInput.click());
  elements.readerFileInput.addEventListener("change", () => loadFile(elements.readerFileInput.files?.[0]));
  elements.readerPrevious.addEventListener("click", () => goToPage(state.currentPage - 1));
  elements.readerNext.addEventListener("click", () => goToPage(state.currentPage + 1));
  elements.readerCurrentPage.addEventListener("change", () => goToPage(elements.readerCurrentPage.value));
  elements.readerZoom.addEventListener("change", () => { state.zoom = Number(elements.readerZoom.value); state.fit = false; renderCurrentPage(); });
  elements.readerFit.addEventListener("click", () => { state.fit = true; renderCurrentPage(); });
  elements.readerRotate.addEventListener("click", () => { state.rotation = (state.rotation + 90) % 360; renderCurrentPage(); });
  elements.readerDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.readerFileInput.click(); });
  elements.readerDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.readerFileInput.click(); } });
  elements.readerDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.readerDropZone.classList.add("is-dragging"); });
  elements.readerDropZone.addEventListener("dragleave", () => elements.readerDropZone.classList.remove("is-dragging"));
  elements.readerDropZone.addEventListener("drop", event => { event.preventDefault(); elements.readerDropZone.classList.remove("is-dragging"); loadFile(event.dataTransfer.files?.[0]); });
  window.addEventListener("resize", () => {
    if (!state.document || (!state.fit && state.zoom > 1)) return;
    window.clearTimeout(state.resizeTimer);
    state.resizeTimer = window.setTimeout(renderCurrentPage, 80);
  });
  window.addEventListener("beforeunload", () => { revokeThumbnails(); if (state.document) state.document.destroy(); });
}
